import { UppyFile } from '@uppy/core';
import { Spinner } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { useTheme } from 'styled-components';
import { IMAGE_MIME_TYPES } from 'src/types';
import { getFileExtension } from 'src/utils/validateFileUpload/fileValidation';
import { readableFileSize } from '../FileUpload/helpers';
import {
  CrossIcon,
  FileCard,
  FileDetails,
  FileName,
  FileNameSpan,
  FileSize,
  Thumbnail,
  ThumbnailIcon,
  ThumbnailImage,
} from './FileThumbnail.styles';

interface Props {
  file: UppyFile;
  handleFileRemove: (id: string) => void;
}

export const FileThumbnail = ({ file, handleFileRemove }: Props) => {
  const theme = useTheme();
  const rawExtension = file.extension || getFileExtension(file.name);
  const fileExtension = rawExtension.startsWith('.')
    ? rawExtension
    : `.${rawExtension}`;
  const fileNameWithoutExtension = file.name.split('.').slice(0, -1).join('.');

  const fileIsImage = file.type ? IMAGE_MIME_TYPES.includes(file.type) : false;
  let thumbnailDisplay: 'loading' | 'image' | 'icon' = 'icon';
  if (fileIsImage) {
    thumbnailDisplay = file.preview ? 'image' : 'loading';
  }

  return (
    <FileCard>
      <Thumbnail>
        {thumbnailDisplay === 'image' && (
          <ThumbnailImage
            src={file.preview}
            alt={file.name}
            data-testid="thumbnail-image"
          />
        )}
        {thumbnailDisplay === 'loading' && (
          <ThumbnailIcon loadingState>
            <Spinner
              size="medium"
              variant="secondary"
              data-testid="thumbnail-spinner"
            />
          </ThumbnailIcon>
        )}
        {thumbnailDisplay === 'icon' && (
          <ThumbnailIcon>
            <StyledIcon
              className="uil uil-file"
              color={theme.atlas.semantic.color.surface.default}
              size="38px"
              data-testid="thumbnail-icon"
            />
          </ThumbnailIcon>
        )}
        <CrossIcon
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleFileRemove(file.id);
          }}
        >
          <StyledIcon
            className="uil uil-multiply"
            color={theme.atlas.semantic.color.outline.disabled}
            size="18px"
          />
        </CrossIcon>
      </Thumbnail>
      <FileDetails>
        <FileName>
          <FileNameSpan>{fileNameWithoutExtension}</FileNameSpan>
          <span>{fileExtension}</span>
        </FileName>
        <FileSize>{readableFileSize(file.size)}</FileSize>
      </FileDetails>
    </FileCard>
  );
};
